import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import {
  ATTENTION_STATUS_REMIND_GROUP_LABEL_KEY,
  getAttentionStatusActions,
  getAttentionStatusRole,
  type IAttentionStatusAction,
  type TAttentionStatusActionId,
} from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { selectCockpitUserAccess } from '../../../../model/selectors'
import {
  CockpitCardCorner,
  type ICockpitCardCornerMenuItem,
  useCockpitCardCornerSlot,
} from '../../../components/cockpitCardCorner'
import { useAttentionCardPartner } from '../../hooks/useAttentionCardPartner'
import type { TAttentionReminderBlock } from '../../hooks/useAttentionReminder'
import { useAttentionReminderRecipients } from '../../hooks/useAttentionReminderRecipients'
import { useAttentionStatusReminders } from '../../hooks/useAttentionStatusReminders'
import { AttentionPartnerLink } from '../attentionPartnerLink'

import styles from './AttentionStatusActions.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.detail
const REMINDER_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.reminder

const BLOCK_KEYS: Record<TAttentionReminderBlock, string> = {
  remindedToday: REMINDER_KEYS.blockedRemindedToday,
  noRecipient: REMINDER_KEYS.blockedNoRecipient,
}

/**
 * Which action leads the corner. The bar's own order puts the way to the section first, and that
 * is the one act no status is opened for: the reader came to the card to answer the status, so
 * what it asks for takes the button and the section keeps its line in the menu.
 */
const LEAD_SLOT_ORDER: IAttentionStatusAction['slot'][] = [
  'primary',
  'secondary',
  'remind',
  'navigation',
]

const pickLead = (actions: IAttentionStatusAction[]) =>
  [...actions].sort(
    (left, right) => LEAD_SLOT_ORDER.indexOf(left.slot) - LEAD_SLOT_ORDER.indexOf(right.slot)
  )[0]

/** Reminders stand together only when there are several — one of them is an act like any other. */
const GROUPED_REMINDERS_FROM = 2

const isReminder = ({ slot }: IAttentionStatusAction) => slot === 'remind'

/** How each slot of the inline bar is drawn; the slot decides the weight, never the status. */
const SLOT_PROPS: Record<
  IAttentionStatusAction['slot'],
  { variant: 'contained' | 'outlined' | 'text'; color?: 'primary' | 'grey' }
> = {
  primary: { variant: 'contained' },
  secondary: { variant: 'outlined' },
  navigation: { variant: 'text' },
  remind: { variant: 'outlined', color: 'grey' },
}

const getTestId = (id: TAttentionStatusActionId) => `attention-status-action-${id}`

interface IAttentionStatusActionsProps {
  bitValue: number
  /** What each action does. An action with no handler is drawn and does nothing, except the
   *  reminders: those the section leaves unhandled go through the card's own reminder. */
  handlers?: Partial<Record<TAttentionStatusActionId, () => void>>
  /** Actions the section draws itself, e.g. the offering switch inside the completeness band. */
  hidden?: TAttentionStatusActionId[]
  /** Actions the status offers but its own state blocks, e.g. signing a plan below 100%. The
   *  card's own reminders are also off, with the reason, when `reminder/check` finds no one to
   *  send them to. */
  disabled?: TAttentionStatusActionId[]
  className?: string
}

/**
 * What the reader's role may do about an opened status. The roles and their actions are the
 * sheet's, held as data in the entity, so a section renders the bar it is given and never asks who
 * is reading it.
 *
 * Inside a card the actions are handed to the card's corner: one leading button for what the
 * status asks for, and the menu for the rest together with the way to the partner's own plan page.
 * Every status then carries its actions in the same place whatever section is on screen.
 *
 * A role with nothing to do on a status draws no bar at all — an empty row of disabled buttons
 * would read as a missing right rather than as a status someone else acts on. The partner link is
 * the card's own way out rather than the status', so it is offered even then.
 */
export const AttentionStatusActions = ({
  bitValue,
  handlers: sectionHandlers,
  hidden = [],
  disabled = [],
  className,
}: IAttentionStatusActionsProps) => {
  const t = useSafetyTranslation()
  const { role } = useSelector(selectCockpitUserAccess)
  const { partnerId, partnerName } = useAttentionCardPartner()
  const corner = useCockpitCardCornerSlot()
  const recipients = useAttentionReminderRecipients()
  const actions = getAttentionStatusActions(bitValue, getAttentionStatusRole(role)).filter(
    ({ id }) => !hidden.includes(id)
  )
  /* The section's own reminders answer for themselves; only the card's are checked. */
  const cardReminderIds = actions
    .filter((action) => isReminder(action) && !sectionHandlers?.[action.id])
    .map(({ id }) => id)
  const statusReminders = useAttentionStatusReminders(bitValue, cardReminderIds)
  const handlers = { ...statusReminders.handlers, ...sectionHandlers }
  const getBlock = (id: TAttentionStatusActionId) =>
    cardReminderIds.includes(id) ? statusReminders.blocks[id] : undefined
  const isDisabled = (id: TAttentionStatusActionId) =>
    disabled.includes(id) ||
    Boolean(getBlock(id)) ||
    (cardReminderIds.includes(id) && statusReminders.checking.includes(id))
  const getHint = (id: TAttentionStatusActionId) => {
    const block = getBlock(id)

    return block ? <Translation span text={BLOCK_KEYS[block]} /> : undefined
  }

  /* Outside a card there is no corner to draw into, so the section keeps the flat bar it used to
     close with. */
  if (!corner) {
    if (actions.length === 0) {
      return null
    }

    return (
      <div className={className ?? styles.root}>
        {actions.map(({ id, slot, labelKey }) => (
          <Button
            key={id}
            size="sm"
            {...SLOT_PROPS[slot]}
            disabled={isDisabled(id)}
            __testId={getTestId(id)}
            onClick={handlers[id]}
          >
            <Translation span text={labelKey} />
          </Button>
        ))}
      </div>
    )
  }

  /* Several reminders are one question with several answers, so they stand under one control and
     the list names the people rather than repeating the verb. A single reminder keeps its own
     button: grouping one act would hide it behind a word it already says. */
  const reminders = actions.filter(isReminder)
  const isGrouped = reminders.length >= GROUPED_REMINDERS_FROM
  const barActions = isGrouped ? actions.filter((action) => !isReminder(action)) : actions

  const lead = pickLead(barActions)
  const remindGroup = isGrouped
    ? {
        id: 'remind',
        label: <Translation span text={ATTENTION_STATUS_REMIND_GROUP_LABEL_KEY} />,
        testId: 'attention-status-actions-remind',
        items: reminders.map(({ id, labelKey, recipient, recipientLabelKey }) => ({
          id,
          /* The recipient's own name where the catalog states one; the act's full wording where
             it does not, so a reminder the catalog grows later is still named. */
          label: <Translation span text={recipientLabelKey ?? labelKey} />,
          /* Why it is off, or who that is where the payload names them. */
          description:
            getHint(id) ?? (recipient && recipients[recipient] ? recipients[recipient] : undefined),
          disabled: isDisabled(id),
          testId: getTestId(id),
          onClick: handlers[id],
        })),
      }
    : undefined

  const menu: ICockpitCardCornerMenuItem[] = [
    /* The way to the partner's own page opens the menu: it is where the card stands, and the acts
       under it are what the status asks for there. */
    ...(partnerId && partnerName
      ? [
          {
            id: 'openPartnerPlan',
            content: (
              <AttentionPartnerLink
                variant="menuItem"
                partnerId={partnerId}
                partnerName={partnerName}
              />
            ),
          },
        ]
      : []),
    ...barActions
      .filter(({ id }) => id !== lead?.id)
      .map(({ id, labelKey }) => ({
        id,
        label: <Translation span text={labelKey} />,
        description: getHint(id),
        disabled: isDisabled(id),
        testId: getTestId(id),
        onClick: handlers[id],
      })),
  ]

  return (
    <CockpitCardCorner
      lead={
        lead && {
          id: lead.id,
          label: <Translation span text={lead.labelKey} />,
          disabled: isDisabled(lead.id),
          hint: getHint(lead.id),
          testId: getTestId(lead.id),
          onClick: handlers[lead.id],
          /* A lead that only leads to the section is a way, not the act the status asks for, so
             it reads as text and leaves the filled weight to the card's own act. */
          variant: lead.slot === 'navigation' ? 'text' : 'contained',
        }
      }
      dropdown={remindGroup}
      menu={menu}
      moreLabel={t(KEYS.moreActions)}
      menuTestId="attention-status-actions-menu"
    />
  )
}

AttentionStatusActions.displayName = 'AttentionStatusActions'
